// /invite/$token — public page (outside the AppShell, styled like /login):
// accepts a workspace invitation. The form depends on whether the e-mail
// already has an account. Works when already signed in too: accepting yields a
// fresh token for the invited workspace.

import { useState, type FormEvent } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { InvitationPreview } from '@goblic/shared'
import { ApiError, ApiUnreachableError, invitationsApi, rateLimitMessage } from '../lib/api-client'
import { switchToToken } from '../lib/auth'
import { formatDateTime, humanize } from '../lib/format'
import { Field } from '../components/ui'
import { LogoMark } from '../components/logo'
import { useIllustrations } from '../lib/illustrations'

const MIN_PASSWORD = 8

function Shell({ children }: { children: React.ReactNode }) {
  const [illustrations] = useIllustrations()
  return (
    <div className="relative min-h-screen overflow-hidden bg-canvas">
      {illustrations ? <div aria-hidden className="login-scene" /> : null}
      <div className="relative mx-auto grid min-h-screen w-full items-center justify-items-center px-4 py-12">
        <div className="w-full max-w-[420px]">
          {illustrations ? (
            <h1 className="mb-4">
              <img
                src="/brand/wordmark.webp"
                alt="Goblic"
                width={1000}
                height={454}
                className="mx-auto h-auto w-full max-w-[420px] select-none drop-shadow-[0_14px_18px_rgb(var(--shadow)/0.16)]"
              />
            </h1>
          ) : (
            <div className="mb-6 flex items-center gap-3">
              <LogoMark size={44} />
              <span className="font-display text-xl font-semibold tracking-[-0.02em] text-ink">
                Goblic
              </span>
            </div>
          )}
          <div className="rounded-[20px] border border-line bg-surface/95 p-8 shadow-overlay backdrop-blur-sm">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

function AcceptForm({ token, preview }: { token: string; preview: InvitationPreview }) {
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const needsRepeat = preview.account !== 'password'
  const needsName = preview.account === 'none'

  const accept = useMutation({
    mutationFn: () =>
      invitationsApi.accept(token, {
        password,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      }),
    onSuccess: (result) => switchToToken(result.token, '/'),
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    setLocalError(null)
    if (needsRepeat && password.length < MIN_PASSWORD) {
      setLocalError(`Use at least ${MIN_PASSWORD} characters for the password.`)
      return
    }
    if (needsRepeat && password !== repeat) {
      setLocalError('The two passwords don’t match.')
      return
    }
    accept.mutate()
  }

  const error = accept.error
  const serverMessage =
    error == null
      ? null
      : error instanceof ApiUnreachableError
        ? `${error.message} Check that the API is running.`
        : rateLimitMessage(error)
          ? rateLimitMessage(error)
        : error instanceof ApiError && error.status === 401
          ? 'That password is incorrect.'
          : error instanceof ApiError
            ? error.message
            : `Couldn’t accept the invitation: ${(error as Error).message}`

  const busy = accept.isPending || accept.isSuccess
  const label =
    preview.account === 'password' ? 'Sign in and join' : 'Create account and join'

  return (
    <form className="mt-7 space-y-4" onSubmit={handleSubmit}>
      {preview.account === 'password' ? (
        <p className="text-base text-ink-2">Sign in as {preview.email} to join.</p>
      ) : (
        <p className="text-base text-ink-2">
          {preview.account === 'none'
            ? `Create your account for ${preview.email}.`
            : `Choose a password for ${preview.email}.`}
        </p>
      )}
      {needsName ? (
        <Field label="Your name" htmlFor="invite-name">
          <input
            id="invite-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            required
            maxLength={80}
            autoComplete="name"
            autoFocus
            className="field-input h-11 rounded-[10px]"
          />
        </Field>
      ) : null}
      <Field label="Password" htmlFor="invite-password">
        <input
          id="invite-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
          autoComplete={needsRepeat ? 'new-password' : 'current-password'}
          autoFocus={!needsName}
          className="field-input h-11 rounded-[10px]"
        />
      </Field>
      {needsRepeat ? (
        <Field
          label="Repeat password"
          htmlFor="invite-repeat"
          hint={`At least ${MIN_PASSWORD} characters.`}
        >
          <input
            id="invite-repeat"
            type="password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            required
            autoComplete="new-password"
            className="field-input h-11 rounded-[10px]"
          />
        </Field>
      ) : null}
      {localError || serverMessage ? (
        <p
          className="rounded-[10px] border border-danger/30 bg-danger-soft px-3 py-2 text-base text-ink"
          role="alert"
        >
          {localError ?? serverMessage}
        </p>
      ) : null}
      <button type="submit" disabled={busy} className="btn-primary h-11 w-full rounded-[10px] text-md">
        {busy ? 'Joining…' : label}
      </button>
    </form>
  )
}

export function InvitePage() {
  const { token } = useParams({ from: '/invite/$token' })
  const previewQuery = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => invitationsApi.preview(token),
    retry: false,
  })
  const preview = previewQuery.data
  const invalid = previewQuery.error instanceof ApiError && previewQuery.error.status === 404

  return (
    <Shell>
      {previewQuery.isLoading ? (
        <p className="text-base text-ink-2">Loading invitation…</p>
      ) : invalid ? (
        <>
          <h2 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
            Invitation unavailable
          </h2>
          <p className="mt-2 text-base text-ink-2">
            This invitation is no longer valid. Ask for a new link.
          </p>
          <Link to="/login" className="btn-secondary mt-6 h-11 w-full rounded-[10px] text-md">
            Go to sign in
          </Link>
        </>
      ) : previewQuery.isError || !preview ? (
        <p
          className="rounded-[10px] border border-danger/30 bg-danger-soft px-3 py-2 text-base text-ink"
          role="alert"
        >
          {previewQuery.error instanceof ApiUnreachableError
            ? `${previewQuery.error.message} Check that the API is running.`
            : rateLimitMessage(previewQuery.error)
              ? rateLimitMessage(previewQuery.error)
            : `Couldn’t load the invitation: ${(previewQuery.error as Error).message}`}
        </p>
      ) : (
        <>
          <h2 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
            {preview.invitedByName ?? 'Someone'} invited you to {preview.workspaceName}
          </h2>
          <p className="mt-1 text-base text-ink-2">
            Your {preview.roles.length === 1 ? 'role' : 'roles'}:{' '}
            {preview.roles.map(humanize).join(', ')}. Valid until{' '}
            {formatDateTime(preview.expiresAt)}.
          </p>
          <AcceptForm token={token} preview={preview} />
        </>
      )}
    </Shell>
  )
}
